import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { Search, Tv, Filter, X, Star, Grid, List as ListIcon, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { PremiumModal } from './PremiumModal';

interface Channel {
  name: string;
  url: string;
  tvg: {
    id: string;
    name: string;
    logo: string;
  };
  group: {
    title: string;
  };
  country?: string;
  language?: string;
  quality?: 'HD' | 'SD' | '4K';
}

interface ChannelListProps {
  channels: Channel[];
  onChannelSelect: (channel: Channel) => void;
  selectedChannelUrl?: string;
  favorites: { url: string; name: string; logo: string }[];
  onToggleFavorite: (channel: Channel) => void;
  viewMode: 'list' | 'grid';
  onViewModeChange: (mode: 'list' | 'grid') => void;
  isPremium?: boolean;
}

const ChannelLogo: React.FC<{ src: string; size?: 'sm' | 'lg' }> = memo(({ src, size = 'sm' }) => {
  const [isInView, setIsInView] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const containerClasses = size === 'lg' 
    ? "w-full aspect-video rounded-xl bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-700 relative shadow-inner group-hover:border-indigo-500/50 transition-colors"
    : "w-10 h-10 rounded bg-gray-800 flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-700 relative shadow-inner";

  return (
    <div ref={imgRef} className={containerClasses}>
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <Tv size={size === 'lg' ? 32 : 18} className="text-gray-600 opacity-50" />
      </div>

      {isInView && src && !hasError && (
        <img 
          src={src} 
          alt="" 
          loading="lazy"
          referrerPolicy="no-referrer"
          draggable="false"
          onLoad={() => setHasLoaded(true)}
          onError={() => setHasError(true)}
          className={`absolute inset-0 w-full h-full object-contain p-2 transition-all duration-500 ${
            hasLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        />
      )}
    </div>
  );
});

const healthCache = new Map<string, 'online' | 'offline' | 'unknown'>();

const HealthIndicator: React.FC<{ url: string; isPremium: boolean }> = memo(({ url, isPremium }) => {
  const [status, setStatus] = useState<'online' | 'offline' | 'unknown'>(healthCache.get(url) || 'unknown');
  const indicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isPremium) return;
    if (healthCache.has(url)) {
      setStatus(healthCache.get(url)!);
      return;
    }

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          
          try {
            // Using a short timeout to prevent blocking
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);
            
            // Note: no-cors mode allows us to see if the server responds at all
            // even if we can't read the response body or status code.
            await fetch(url, { 
              method: 'HEAD', 
              mode: 'no-cors',
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            healthCache.set(url, 'online');
            setStatus('online');
          } catch (err) {
            healthCache.set(url, 'offline');
            setStatus('offline');
          }
        }
      },
      { rootMargin: '100px' }
    );

    if (indicatorRef.current) {
      observer.observe(indicatorRef.current);
    }

    return () => observer.disconnect();
  }, [url, isPremium]);

  if (!isPremium) return null;

  return (
    <div ref={indicatorRef} className="flex items-center gap-1.5 ml-2">
      <div className={`w-1.5 h-1.5 rounded-full ${
        status === 'online' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' :
        status === 'offline' ? 'bg-red-500' :
        'bg-gray-600'
      }`} />
      <span className={`text-[8px] font-black uppercase tracking-widest ${
        status === 'online' ? 'text-emerald-500/80' :
        status === 'offline' ? 'text-red-500/80' :
        'text-gray-600'
      }`}>
        {status}
      </span>
    </div>
  );
});

HealthIndicator.displayName = 'HealthIndicator';

ChannelLogo.displayName = 'ChannelLogo';

const ChannelItem = memo(({ 
  channel, 
  isSelected, 
  isFav, 
  viewMode, 
  onSelect, 
  onToggleFav, 
  isPremium,
  onPremiumClick,
  innerRef 
}: { 
  channel: Channel; 
  isSelected: boolean; 
  isFav: boolean; 
  viewMode: 'list' | 'grid';
  onSelect: (c: Channel) => void;
  onToggleFav: (channel: Channel) => void;
  isPremium: boolean;
  onPremiumClick: (feature: string, desc: string) => void;
  innerRef?: React.Ref<any>;
}) => {
  if (viewMode === 'list') {
    return (
      <div 
        ref={innerRef}
        className={`relative group border-b border-gray-800/30 ${isSelected ? 'bg-indigo-600/10' : 'hover:bg-gray-800/40'}`}
      >
        <button
          onClick={() => onSelect(channel)}
          className={`w-full flex items-center gap-3 p-3 transition-colors text-left pl-4 pr-12 cursor-pointer ${
            isSelected ? 'border-l-4 border-indigo-600' : ''
          }`}
        >
          <ChannelLogo src={channel.tvg.logo} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${
              isSelected ? 'text-indigo-400' : 'text-gray-100'
            }`}>
              {channel.name}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[10px] text-gray-500 truncate uppercase tracking-wider font-semibold">
                {channel.group.title && channel.group.title.toLowerCase() !== 'undefined' ? channel.group.title : 'General'}
              </p>
              <HealthIndicator url={channel.url} isPremium={isPremium} />
            </div>
          </div>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isPremium) {
              onPremiumClick("Favorites", "Create your own personalized list of most-watched streams with cloud sync across all your devices.");
              return;
            }
            onToggleFav(channel);
          }}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all cursor-pointer ${
            isFav ? 'text-yellow-500 opacity-100' : 'text-gray-600 opacity-0 group-hover:opacity-100 hover:text-gray-400'
          }`}
        >
          {isPremium ? (
            <Star size={16} fill={isFav ? 'currentColor' : 'none'} />
          ) : (
            <Lock size={12} className="opacity-50" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={innerRef}
      className="relative group bg-gray-800/30 rounded-2xl border border-gray-800 hover:border-indigo-500/50 transition-all overflow-hidden shadow-lg p-2"
    >
      <button
        onClick={() => onSelect(channel)}
        className="w-full text-left cursor-pointer"
      >
        <ChannelLogo src={channel.tvg.logo} size="lg" />
        <div className="mt-3 px-2 pb-2">
           <p className={`text-sm font-bold truncate ${isSelected ? 'text-indigo-400' : 'text-gray-100'}`}>
             {channel.name}
           </p>
           <div className="flex items-center justify-between mt-1">
             <p className="text-[10px] text-gray-500 truncate uppercase font-bold tracking-widest">
               {channel.group.title || 'General'}
             </p>
             <HealthIndicator url={channel.url} isPremium={isPremium} />
           </div>
        </div>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!isPremium) {
            onPremiumClick("Favorites", "Create your own personalized list of most-watched streams with cloud sync across all your devices.");
            return;
          }
          onToggleFav(channel);
        }}
        className={`absolute top-4 right-4 p-2 bg-black/40 backdrop-blur-md rounded-xl transition-all cursor-pointer border border-white/5 shadow-xl ${
          isFav ? 'text-yellow-500 scale-110' : 'text-white/40 opacity-0 group-hover:opacity-100 hover:text-white'
        }`}
      >
        {isPremium ? (
          <Star size={18} fill={isFav ? 'currentColor' : 'none'} />
        ) : (
          <Lock size={14} className="opacity-50" />
        )}
      </button>
      {isSelected && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
      )}
    </div>
  );
});

ChannelItem.displayName = 'ChannelItem';

export const ChannelList: React.FC<ChannelListProps> = ({ 
  channels, 
  onChannelSelect, 
  selectedChannelUrl,
  favorites,
  onToggleFavorite,
  viewMode,
  onViewModeChange,
  isPremium = false
}) => {
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [visibleCount, setVisibleCount] = useState(50);
  const activeChannelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [selectedQuality, setSelectedQuality] = useState('All');

  // Derive filter options
  const countries = useMemo(() => {
    const cSet = new Set(channels.map(c => c.country).filter(Boolean));
    return ['All', ...Array.from(cSet).sort()];
  }, [channels]);

  const languages = useMemo(() => {
    const lSet = new Set(channels.map(c => c.language).filter(Boolean));
    return ['All', ...Array.from(lSet).sort()];
  }, [channels]);

  const qualities = ['All', '4K', 'HD', 'SD'];

  // Modal State
  const [premiumModal, setPremiumModal] = useState<{ open: boolean, name: string, desc: string }>({
    open: false,
    name: '',
    desc: ''
  });

  const groups = useMemo(() => {
    const g = new Set(channels.map(c => c.group.title || 'Other'));
    const sortedGroups = Array.from(g).sort();
    return isPremium 
      ? ['All', 'Favorites', ...sortedGroups]
      : ['All', ...sortedGroups];
  }, [channels, isPremium]);

  const filteredChannels = useMemo(() => {
    return channels.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase());
      const isFav = favorites.some(f => f.url === c.url);
      
      let matchesGroup = true;
      if (selectedGroup === 'Favorites') {
        matchesGroup = isFav;
      } else if (selectedGroup !== 'All') {
        matchesGroup = (c.group.title || 'Other') === selectedGroup;
      }

      const matchesCountry = selectedCountry === 'All' || c.country === selectedCountry;
      const matchesLanguage = selectedLanguage === 'All' || c.language === selectedLanguage;
      const matchesQuality = selectedQuality === 'All' || c.quality === selectedQuality;
      
      return matchesSearch && matchesGroup && matchesCountry && matchesLanguage && matchesQuality;
    });
  }, [channels, search, selectedGroup, favorites, selectedCountry, selectedLanguage, selectedQuality]);

  useEffect(() => {
    setVisibleCount(50);
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, [search, selectedGroup]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && visibleCount < filteredChannels.length) {
        setVisibleCount(prev => prev + 50);
      }
    }, { root: listContainerRef.current, rootMargin: '400px' });

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [visibleCount, filteredChannels.length]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [groups]);

  const scrollGroups = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (selectedChannelUrl && activeChannelRef.current) {
      activeChannelRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedChannelUrl]);

  const triggerPremiumModal = useCallback((name: string, desc: string) => {
    setPremiumModal({ open: true, name, desc });
  }, []);

  return (
    <div className="flex flex-col h-full bg-gray-900 border-r border-gray-800 w-full">
      <PremiumModal 
        isOpen={premiumModal.open}
        onClose={() => setPremiumModal(prev => ({ ...prev, open: false }))}
        featureName={premiumModal.name}
        featureDescription={premiumModal.desc}
      />

      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search channels..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
            <button
              onClick={() => {
                if (!isPremium) {
                  triggerPremiumModal("Advanced Filters", "Fine-tune your library by Country, Language, and Video Quality (4K/HD/SD) to find exactly what you're looking for.");
                  return;
                }
                setShowAdvancedFilters(!showAdvancedFilters);
              }}
              className={`p-1.5 rounded transition-all relative ${showAdvancedFilters ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-200 cursor-pointer'}`}
              title="Advanced Filters"
            >
              <Filter size={16} />
              {!isPremium && <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border border-gray-900" />}
            </button>
            <div className="w-px h-4 bg-gray-700 mx-1 self-center" />
            <button
              onClick={() => onViewModeChange('list')}
              className={`p-1.5 rounded transition-all ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-200 cursor-pointer'}`}
              title="List View"
            >
              <ListIcon size={16} />
            </button>
            <button
              onClick={() => {
                if (!isPremium) {
                  triggerPremiumModal("Grid View", "Experience a modern, media-rich layout with large logos and smooth transitions—Netflix style.");
                  return;
                }
                onViewModeChange('grid');
              }}
              className={`p-1.5 rounded transition-all relative ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-200 cursor-pointer'}`}
              title="Grid View"
            >
              <Grid size={16} />
              {!isPremium && <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border border-gray-900" />}
            </button>
          </div>
        </div>

        {showAdvancedFilters && isPremium && (
          <div className="bg-gray-850 p-4 rounded-xl border border-gray-800 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Country</label>
                <select 
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg py-1.5 px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Language</label>
                <select 
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg py-1.5 px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {languages.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Stream Quality</label>
              <div className="flex gap-2">
                {qualities.map(q => (
                  <button
                    key={q}
                    onClick={() => setSelectedQuality(q)}
                    className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all border ${
                      selectedQuality === q 
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/20' 
                        : 'bg-gray-800 border-gray-700 text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <button 
              onClick={() => {
                setSelectedCountry('All');
                setSelectedLanguage('All');
                setSelectedQuality('All');
              }}
              className="w-full py-1.5 text-[10px] font-bold text-gray-500 hover:text-white transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

        <div className="relative group/scroll">
          {showLeftArrow && (
            <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-r from-gray-900 via-gray-900 to-transparent pr-8">
              <button 
                onClick={() => scrollGroups('left')}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded-full border border-gray-700 text-gray-300 transition-all cursor-pointer shadow-lg"
              >
                <ChevronLeft size={14} />
                <span className="sr-only">Scroll Left</span>
              </button>
            </div>
          )}

          <div 
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide w-full max-w-full custom-scrollbar-horizontal"
          >
            <div className="flex items-center gap-2 min-w-max">
              {groups.map(group => (
                <button
                  key={group}
                  onClick={() => setSelectedGroup(group)}
                  className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                    selectedGroup === group
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {group === 'Favorites' && <Star size={12} className={selectedGroup === group ? 'fill-white' : ''} />}
                  {group}
                </button>
              ))}
            </div>
          </div>

          {showRightArrow && (
            <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-l from-gray-900 via-gray-900 to-transparent pl-8">
              <button 
                onClick={() => scrollGroups('right')}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded-full border border-gray-700 text-gray-300 transition-all cursor-pointer shadow-lg"
              >
                <ChevronRight size={14} />
                <span className="sr-only">Scroll Right</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div 
        ref={listContainerRef}
        className="flex-1 overflow-y-auto custom-scrollbar"
      >
        {filteredChannels.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center">
            <div className="w-16 h-16 bg-gray-800/50 rounded-2xl flex items-center justify-center mb-4 border border-gray-700/50">
              <Tv size={32} className="text-gray-600" />
            </div>
            <p className="text-sm font-medium text-gray-400">No channels found</p>
            <p className="text-xs text-gray-500 mt-1 max-w-[200px]">Try adjusting your search or category filter.</p>
          </div>
        ) : (
          <div className={viewMode === 'list' ? '' : 'grid grid-cols-1 gap-4 p-4'}>
            {filteredChannels.slice(0, visibleCount).map((channel, idx) => {
              const isSelected = selectedChannelUrl === channel.url;
              const isFav = favorites.some(f => f.url === channel.url);
              return (
                <ChannelItem 
                  key={`${channel.url}-${idx}`}
                  channel={channel}
                  isSelected={isSelected}
                  isFav={isFav}
                  viewMode={viewMode}
                  isPremium={isPremium}
                  onSelect={onChannelSelect}
                  onToggleFav={onToggleFavorite}
                  onPremiumClick={triggerPremiumModal}
                  innerRef={isSelected ? activeChannelRef : null}
                />
              );
            })}
            
            <div ref={sentinelRef} className="h-4 w-full" />
            
            {visibleCount < filteredChannels.length && (
              <div className="p-4 text-center text-xs text-gray-500 italic">
                Loading more channels...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

